import React, { useState } from 'react';
import { CheckCircle2, PauseCircle, AlertCircle } from 'lucide-react';

// CSS для приховування стрілочок в number inputs та шрифти
export const hideSpinnersStyle = `
  @import url('https://fonts.googleapis.com/css2?family=Archivo+Black&display=swap');
  @import url('https://fonts.googleapis.com/css2?family=Roboto:wght@300;400;500;600;700&display=swap');
  
  * {
    font-family: 'Roboto', sans-serif;
  }
  
  .logo-text {
    font-family: 'Archivo Black', sans-serif;
    font-weight: 400;
    color: #163248;
  }
  
  input[type="number"]::-webkit-outer-spin-button,
  input[type="number"]::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
  input[type="number"] {
    -moz-appearance: textfield;
  }
  
  /* Прибираємо синій контур focus на кнопках */
  button:focus,
  button:focus-visible {
    outline: none !important;
    box-shadow: none !important;
  }

  /* Антизум для мобільних iOS — тільки на маленьких екранах */
  @media (max-width: 768px) {
    input, select, textarea {
      font-size: 16px !important;
    }
  }

  /* Ховаємо скролбар в модалках але залишаємо скрол */
  .modal-scroll::-webkit-scrollbar,
  .modal-no-scrollbar::-webkit-scrollbar {
    display: none;
    width: 0;
    height: 0;
  }
  .modal-scroll,
  .modal-no-scrollbar {
    -ms-overflow-style: none;
    scrollbar-width: none;
  }
`;

export const ITEM_TYPES = {
  'Робота': { letter: 'Р', color: 'bg-green-500', textColor: 'text-green-700' },
  'Матеріали': { letter: 'М', color: 'bg-yellow-500', textColor: 'text-yellow-700' },
  'Комплектація': { letter: 'К', color: 'bg-blue-500', textColor: 'text-blue-700' },
  'Транспорт': { letter: 'Т', color: 'bg-orange-500', textColor: 'text-orange-700' },
  'Інші послуги': { letter: 'І', color: 'bg-purple-500', textColor: 'text-purple-700' },
};

// Статуси об'єкта
export const STATUS_CONFIG = {
  active: { 
    label: 'Активний', 
    bgColor: 'bg-green-100', 
    textColor: 'text-green-700',
    icon: CheckCircle2
  },
  paused: { 
    label: 'Призупинений', 
    bgColor: 'bg-amber-100', 
    textColor: 'text-amber-700',
    icon: PauseCircle
  },
  closed: { 
    label: 'Закритий', 
    bgColor: 'bg-gray-200', 
    textColor: 'text-gray-600',
    icon: AlertCircle
  }
};

// Типи об'єктів
export const OBJECT_TYPES = {
  apartment: { label: 'Квартира', icon: '🏢' },
  house: { label: 'Будинок', icon: '🏠' },
  duplex: { label: 'Дуплекс', icon: '🏘️' },
  townhouse: { label: 'Таунхаус', icon: '🏡' },
  commercial: { label: 'Комерція', icon: '🏪' },
  other: { label: 'Інше', icon: '📍' }
};

export const UNITS = [
  'шт.', 'м', 'м.п.', 'м²', 'м³',
  'кг', 'т', 'л',
  'пак.', 'міш.', 'компл.',
  'посл.', 'год.', 'дн.', 'тиж.', 'міс.',
  '%'
];

export const TypeBadge = ({ type, showLabel = false, ringColor = null }) => {
  const config = ITEM_TYPES[type] || ITEM_TYPES['Робота'];
  return (
    <div className="flex items-center gap-2">
      <span
        className={`w-6 h-6 ${config.color} text-white text-xs font-bold rounded-full flex items-center justify-center`}
        style={ringColor ? { boxShadow: `0 0 0 2px #fff, 0 0 0 4px ${ringColor}` } : {}}
      >
        {config.letter}
      </span>
      {showLabel && <span className="text-sm">{type}</span>}
    </div>
  );
};

export const TypeSelect = ({ value, onChange, ringColor = null }) => {
  const [isOpen, setIsOpen] = useState(false);
  const buttonRef = React.useRef(null);
  const [menuPos, setMenuPos] = useState({ top: 0, left: 0 });
  
  const handleOpen = () => {
    if (buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setMenuPos({
        top: rect.bottom + 4,
        left: rect.left
      });
    }
    setIsOpen(!isOpen);
  };
  
  return (
    <div className="relative" style={{ zIndex: isOpen ? 200 : 'auto' }}>
      <button
        ref={buttonRef}
        onClick={handleOpen}
        className="flex items-center justify-center p-1 hover:bg-gray-100 rounded transition-colors"
      >
        <TypeBadge type={value} ringColor={ringColor} />
      </button>
      
      {isOpen && (
        <>
          <div 
            className="fixed inset-0" 
            style={{ zIndex: 9998 }}
            onClick={() => setIsOpen(false)} 
          />
          <div 
            className="fixed bg-white border rounded-lg shadow-xl py-1 min-w-[160px]"
            style={{ top: menuPos.top, left: menuPos.left, zIndex: 9999 }}
          >
            {Object.keys(ITEM_TYPES).map((type) => (
              <button
                key={type}
                onClick={() => {
                  onChange(type);
                  setIsOpen(false);
                }}
                className={`w-full px-3 py-2 flex items-center gap-2 hover:bg-gray-100 transition-colors ${value === type ? 'bg-gray-50' : ''}`}
              >
                <TypeBadge type={type} showLabel />
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
};
